// src/pages/MapPage.tsx
//
// Full-screen interactive map. Initializes centered on user, can be
// panned freely (camera does NOT follow user dot — Google Maps style,
// not car-nav style). User can recenter with a button.
//
// Renders pinned matches as destination pins, filtered to those within
// 20 km of the user's current location. Tap a pin → bottom sheet with
// "Get directions" + "Unpin" actions.
//
// Directions mode: when user picks "Take me there", the page enters
// a single-destination state with a live walking polyline from user
// to that destination. Polyline auto-refreshes as user moves >50m
// (handled by useWalkingRoute's coord-bucket cache invalidation).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Button,
  Divider,
  Fab,
  CircularProgress,
} from '@mui/material';
import {
  MyLocationOutlined,
  Close,
  DirectionsWalkOutlined,
  PushPinOutlined,
  PlaceOutlined,
  ChatBubbleOutline,
} from '@mui/icons-material';
import {
  Map as GMap,
  AdvancedMarker,
  useMap,
  useApiIsLoaded,
} from '@vis.gl/react-google-maps';
import { useNavigate } from 'react-router-dom';
import { useLocation } from '../hooks/useLocation';
import { usePinned, usePinActions } from '../hooks/usePinned';
import { useWalkingRoute } from '../hooks/useWalkingRoute';
import { UserLocationMarker } from '../components/map/UserLocationMarker';
import { useT } from '../i18n/useT';

const PIN_RADIUS_KM = 20;

function haversineKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const UserDot: React.FC = () => <UserLocationMarker size={18} />;

const DestPin: React.FC<{ active?: boolean }> = ({ active }) => (
  <Box
    sx={{
      width: active ? 28 : 22,
      height: active ? 28 : 22,
      borderRadius: '50%',
      bgcolor: active ? '#fbbf24' : '#ea4335',
      border: '3px solid white',
      boxShadow: '0 2px 10px rgba(0,0,0,0.35)',
      transition: 'all 0.15s ease',
    }}
  />
);

/** Small white-pill title rendered just below a destination pin.
 *  Style mirrors Google Maps' inline place labels — short, glanceable,
 *  truncated rather than wrapped so it doesn't dominate the map.
 *  Composed alongside <DestPin> inside an AdvancedMarker. */
const PinLabel: React.FC<{ title: string }> = ({ title }) => (
  <Box
    sx={{
      position: 'absolute',
      // Sit just under the pin. AdvancedMarker centers its content
      // on the lat/lng, so we offset downward by half-pin + a gap.
      top: 'calc(50% + 18px)',
      left: '50%',
      transform: 'translateX(-50%)',
      bgcolor: 'rgba(255,255,255,0.96)',
      color: 'rgba(0,0,0,0.78)',
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1.2,
      px: 0.75,
      py: 0.25,
      borderRadius: 0.75,
      boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
      maxWidth: 140,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      pointerEvents: 'none',
    }}
  >
    {title}
  </Box>
);

/** Imperative recenter command via map ref. Lives inside <Map> so it
 *  can use the useMap hook. */
const RecenterController: React.FC<{
  recenterTo: { lat: number; lng: number; zoom?: number } | null;
  onConsumed: () => void;
}> = ({ recenterTo, onConsumed }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !recenterTo) return;
    map.panTo({ lat: recenterTo.lat, lng: recenterTo.lng });
    if (recenterTo.zoom !== undefined) {
      map.setZoom(recenterTo.zoom);
    }
    onConsumed();
  }, [map, recenterTo, onConsumed]);
  return null;
};

/** Draws the walking-route polyline. Lives inside <Map>. Owned by the
 *  navigation state so we can clear it cleanly. */
const RouteOverlay: React.FC<{
  user: { lat: number; lng: number };
  destination: { lat: number; lng: number };
}> = ({ user, destination }) => {
  const map = useMap();
  const isLoaded = useApiIsLoaded();
  const { polyline } = useWalkingRoute(destination, user);
  const polylineRef = useRef<google.maps.Polyline | null>(null);

  useEffect(() => {
    if (!map || !isLoaded) return;
    if (!polyline || polyline.length === 0) {
      polylineRef.current?.setMap(null);
      polylineRef.current = null;
      return;
    }
    if (!polylineRef.current) {
      polylineRef.current = new google.maps.Polyline({
        strokeOpacity: 0,
        icons: [
          {
            icon: {
              path: 'M 0,-1 0,1',
              strokeOpacity: 1,
              strokeColor: '#1a73e8',
              strokeWeight: 5,
              scale: 4,
            },
            offset: '0',
            repeat: '14px',
          },
        ],
      });
      polylineRef.current.setMap(map);
    }
    polylineRef.current.setPath(polyline);
  }, [map, isLoaded, polyline]);

  // Tear down on unmount
  useEffect(() => {
    return () => {
      polylineRef.current?.setMap(null);
      polylineRef.current = null;
    };
  }, []);

  return null;
};

/** Auto-fits bounds to user + destination once when navigation starts. */
const NavigationBoundsFitter: React.FC<{
  user: { lat: number; lng: number };
  destination: { lat: number; lng: number };
  navTargetId: string;
}> = ({ user, destination, navTargetId }) => {
  const map = useMap();
  const isLoaded = useApiIsLoaded();
  const fittedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!map || !isLoaded) return;
    if (fittedFor.current === navTargetId) return;
    const bounds = new google.maps.LatLngBounds();
    bounds.extend(user);
    bounds.extend(destination);
    map.fitBounds(bounds, { top: 80, right: 60, bottom: 220, left: 60 });
    fittedFor.current = navTargetId;
  }, [
    map,
    isLoaded,
    navTargetId,
    user.lat,
    user.lng,
    destination.lat,
    destination.lng,
  ]);

  return null;
};

export const MapPage: React.FC = () => {
  const t = useT();
  const navigate = useNavigate();
  const { location } = useLocation();
  const { pinned } = usePinned();
  const { unpin } = usePinActions();

  const user =
    location?.latitude && location?.longitude
      ? { lat: location.latitude, lng: location.longitude }
      : null;

  // Pinned items + within 20km of user.
  const visiblePins = useMemo(() => {
    if (!user) return [];
    return pinned.filter((m) => {
      if (
        typeof m.location?.lat !== 'number' ||
        typeof m.location?.lng !== 'number'
      )
        return false;
      const km = haversineKm(
        user.lat,
        user.lng,
        m.location.lat,
        m.location.lng
      );
      return km <= PIN_RADIUS_KM;
    });
  }, [pinned, user?.lat, user?.lng]);

  const [selectedPinId, setSelectedPinId] = useState<string | null>(null);
  const selectedPin =
    visiblePins.find((p) => p.id === selectedPinId) || null;

  const [navTarget, setNavTarget] = useState<{
    id: string;
    lat: number;
    lng: number;
    title: string;
  } | null>(null);

  // Distance + duration text in the navigation banner. The polyline
  // itself is drawn by RouteOverlay; here we just want the metadata.
  const { distanceM, durationS } = useWalkingRoute(
    navTarget ? { lat: navTarget.lat, lng: navTarget.lng } : null,
    navTarget && user ? user : null
  );

  // Recenter command — set this to trigger pan/zoom inside the map.
  const [recenterCmd, setRecenterCmd] = useState<{
    lat: number;
    lng: number;
    zoom?: number;
  } | null>(null);

  // Initial center: user's location when first available.
  const initialCenter = user || { lat: 0, lng: 0 };
  const [hasInitialized, setHasInitialized] = useState(false);
  useEffect(() => {
    if (!hasInitialized && user) {
      setRecenterCmd({ lat: user.lat, lng: user.lng, zoom: 14 });
      setHasInitialized(true);
    }
  }, [user, hasInitialized]);

  const mapId =
    (import.meta as any).env?.VITE_GOOGLE_MAPS_MAP_ID || undefined;

  const handlePinTap = (pinId: string) => {
    setSelectedPinId(pinId);
  };
  const handleStartNavigation = () => {
    if (!selectedPin || !user) return;
    if (
      typeof selectedPin.location?.lat !== 'number' ||
      typeof selectedPin.location?.lng !== 'number'
    )
      return;
    setNavTarget({
      id: selectedPin.id,
      lat: selectedPin.location.lat,
      lng: selectedPin.location.lng,
      title: selectedPin.title || t('Destination'),
    });
    setSelectedPinId(null);
  };
  const handleStopNavigation = () => {
    setNavTarget(null);
  };
  const handleUnpin = async () => {
    if (!selectedPin) return;
    await unpin(selectedPin.id);
    setSelectedPinId(null);
  };
  const handleAskAI = () => {
    if (!selectedPin) return;
    const prompt =
      (selectedPin as any).chat_prompt ||
      `Tell me about ${selectedPin.title}`;
    navigate(`/chat?initial=${encodeURIComponent(prompt)}`);
  };

  if (!user) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2,
          p: 4,
          textAlign: 'center',
        }}
      >
        <CircularProgress size={36} />
        <Typography variant="body1" color="text.secondary">
          
          {t("Getting your location…")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          
          {t("The map needs your location to show what's nearby.")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <GMap
        defaultCenter={initialCenter}
        defaultZoom={14}
        mapId={mapId}
        gestureHandling="greedy"
        disableDefaultUI={true}
        clickableIcons={false}
        keyboardShortcuts={false}
        style={{ width: '100%', height: '100%' }}
      >
        <RecenterController
          recenterTo={recenterCmd}
          onConsumed={() => setRecenterCmd(null)}
        />

        {/* User dot — always rendered */}
        <AdvancedMarker position={user}>
          <UserDot />
        </AdvancedMarker>

        {/* Pin markers — hidden during navigation so the active
            destination is the only pin visible. */}
        {!navTarget &&
          visiblePins.map((pin) => {
            if (
              typeof pin.location?.lat !== 'number' ||
              typeof pin.location?.lng !== 'number'
            )
              return null;
            const isSelected = pin.id === selectedPinId;
            return (
              <AdvancedMarker
                key={pin.id}
                position={{
                  lat: pin.location.lat,
                  lng: pin.location.lng,
                }}
                onClick={() => handlePinTap(pin.id)}
              >
                {/* Wrapper so the title pill can be absolutely
                    positioned under the pin. The wrapper itself
                    is the AdvancedMarker anchor target. */}
                <Box sx={{ position: 'relative' }}>
                  <DestPin active={isSelected} />
                  {pin.title && <PinLabel title={pin.title} />}
                </Box>
              </AdvancedMarker>
            );
          })}

        {/* Active navigation target pin */}
        {navTarget && (
          <AdvancedMarker
            position={{ lat: navTarget.lat, lng: navTarget.lng }}
          >
            <DestPin active />
          </AdvancedMarker>
        )}

        {/* Walking polyline — only when navigating */}
        {navTarget && (
          <RouteOverlay
            user={user}
            destination={{ lat: navTarget.lat, lng: navTarget.lng }}
          />
        )}

        {/* Auto-fit bounds when navigation starts */}
        {navTarget && (
          <NavigationBoundsFitter
            user={user}
            destination={{ lat: navTarget.lat, lng: navTarget.lng }}
            navTargetId={navTarget.id}
          />
        )}
      </GMap>

      {/* Top-of-map navigation banner */}
      {navTarget && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            right: 12,
            bgcolor: 'rgba(255,255,255,0.97)',
            backdropFilter: 'blur(8px)',
            borderRadius: 3,
            boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            zIndex: 5,
          }}
        >
          <DirectionsWalkOutlined sx={{ color: 'primary.main' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {navTarget.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {durationS != null
                ? `${Math.max(1, Math.round(durationS / 60))} min`
                : 'calculating…'}
              {distanceM != null
                ? ` · ${
                    distanceM < 1000
                      ? `${Math.round(distanceM)} m`
                      : `${(distanceM / 1000).toFixed(1)} km`
                  }`
                : ''}
            </Typography>
          </Box>
          <IconButton onClick={handleStopNavigation} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Recenter button (bottom-right) */}
      <Fab
        size="medium"
        color="default"
        onClick={() =>
          setRecenterCmd({ lat: user.lat, lng: user.lng, zoom: 16 })
        }
        sx={{
          position: 'absolute',
          right: 16,
          bottom: 16,
          bgcolor: 'background.paper',
          '&:hover': { bgcolor: 'background.paper' },
          zIndex: 5,
        }}
        aria-label={t("Recenter on my location")}
      >
        <MyLocationOutlined />
      </Fab>

      {/* Pin counter (top-left) */}
      {!navTarget && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            bgcolor: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(8px)',
            borderRadius: 999,
            px: 1.5,
            py: 0.75,
            boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            zIndex: 5,
          }}
        >
          <PlaceOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography
            variant="caption"
            sx={{ fontSize: '0.75rem', fontWeight: 600 }}
          >
            {visiblePins.length}  {t("pinned · within")} {PIN_RADIUS_KM}  {t("km")}
          </Typography>
        </Box>
      )}

      {/* Pin detail bottom sheet */}
      <Drawer
        anchor="bottom"
        open={!!selectedPin}
        onClose={() => setSelectedPinId(null)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '60vh',
          },
        }}
      >
        {selectedPin && (
          <Box sx={{ p: 2.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
              }}
            >
              <PushPinOutlined
                sx={{ fontSize: 16, color: 'text.secondary' }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'text.secondary',
                }}
              >
                
                {t("Pinned")}
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              {selectedPin.title}
            </Typography>
            {selectedPin.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, lineHeight: 1.5 }}
              >
                {selectedPin.description}
              </Typography>
            )}
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<DirectionsWalkOutlined />}
                onClick={handleStartNavigation}
                sx={{ flex: '1 1 140px' }}
              >
                
                {t("Take me there")}
              </Button>
              <Button
                variant="outlined"
                startIcon={<ChatBubbleOutline />}
                onClick={handleAskAI}
                sx={{ flex: '1 1 100px' }}
              >
                
                {t("Ask AI")}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Close />}
                onClick={handleUnpin}
                sx={{ flex: '1 1 100px' }}
              >
                
                {t("Unpin")}
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};
